import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { BACKEND_URL } from '../config';
import './VideoGamesList.css';

const VARIANT_LABELS = {
  loose: 'Loose',
  cib: 'Complete In Box',
  new: 'New Sealed'
};

const VideoGamesList = ({
  userSettings,
  addToCart,
  exchangeRates,
  consoleFilter = '',
  searchTerm = '',
  onConsoleChange = () => {}
}) => {
  const { t } = useTranslation();
  const [videoGames, setVideoGames] = useState([]);
  const [consoles, setConsoles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [selectedQuantities, setSelectedQuantities] = useState({});
  const DEFAULT_CONSOLE = 'Playstation 5';
  const [localSearchTerm, setLocalSearchTerm] = useState(searchTerm);
  const [localConsole, setLocalConsole] = useState(consoleFilter || DEFAULT_CONSOLE);
  const [upcSearch, setUpcSearch] = useState('');
  const initialFetchDone = useRef(false);

  const videoGameSettings = userSettings?.video_game_settings || {
    enabled_variants: ['loose', 'cib', 'new'],
    default_variant: 'cib',
    variant_percentages: {
      loose: { cash_percentage: 40, credit_percentage: 55, enabled: true },
      cib: { cash_percentage: 45, credit_percentage: 60, enabled: true },
      new: { cash_percentage: 50, credit_percentage: 65, enabled: true }
    },
    floor_price: 1.00
  };

  // Fetch consoles on mount
  useEffect(() => {
    const fetchConsoles = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/videogames/consoles?username=${userSettings?.username || ''}`);
        const data = await response.json();
        if (data.success) {
          setConsoles(data.consoles);
        }
      } catch (error) {
        console.error('Error fetching consoles:', error);
      }
    };
    fetchConsoles();
  }, [userSettings?.username]);

  // Fetch video games
  const fetchVideoGames = useCallback(async (page = 1, consoleVal = '', searchVal = '', upcVal = '') => {
    console.log('VideoGamesList: fetchVideoGames called with:', { page, consoleVal, searchVal, upcVal });
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '25',
        username: userSettings?.username || ''
      });
      if (searchVal) params.append('q', searchVal);
      if (consoleVal) params.append('console', consoleVal);
      if (upcVal) params.append('upc', upcVal);

      const url = `${BACKEND_URL}/videogames/search?${params}`;
      console.log('VideoGamesList: Fetching from URL:', url);
      const response = await fetch(url);
      const data = await response.json();

      console.log('VideoGamesList: Response received:', data);
      if (data.success) {
        setVideoGames(data.items || []);
        setTotalPages(data.totalPages || 1);
        setCurrentPage(data.currentPage || 1);
        setTotalCount(data.totalCount || 0);
      } else {
        console.error('VideoGamesList: Fetch failed:', data);
        setVideoGames([]);
      }
    } catch (error) {
      console.error('VideoGamesList: Error fetching:', error);
      setVideoGames([]);
    } finally {
      setIsLoading(false);
    }
  }, [userSettings?.username]);

  // Initial fetch on mount - default to PlayStation 5 if no console specified
  useEffect(() => {
    console.log('VideoGamesList: Component mounted, initialFetchDone:', initialFetchDone.current);
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      const initialConsole = consoleFilter || DEFAULT_CONSOLE;
      console.log('VideoGamesList: Triggering initial fetch with console:', initialConsole);
      fetchVideoGames(1, initialConsole, '', '');
    }
  }, [fetchVideoGames, consoleFilter]);

  // Refetch when console changes
  const handleConsoleChange = (newConsole) => {
    setLocalConsole(newConsole);
    onConsoleChange(newConsole);
    fetchVideoGames(1, newConsole, localSearchTerm, upcSearch);
  };

  // Get currency symbol
  const getCurrencySymbol = (currency) => {
    const symbols = { USD: '$', EUR: '€', GBP: '£', CAD: 'C$', AUD: 'A$', JPY: '¥' };
    return symbols[currency] || currency + ' ';
  };

  const userCurrency = userSettings?.currency || 'USD';
  const currencySymbol = getCurrencySymbol(userCurrency);

  // Convert USD to user's currency
  const convertCurrency = (usdAmount) => {
    if (!usdAmount || userCurrency === 'USD') return usdAmount;
    const rate = exchangeRates?.[userCurrency] || 1;
    return usdAmount * rate;
  };

  // Calculate price for a video game
  const calculatePrice = (game, variant) => {
    const buyPriceUSD = game.prices?.[variant]?.buy || 0;
    if (!buyPriceUSD) return { cashPrice: 0, creditPrice: 0, lowPrice: 0 };

    const variantSettings = videoGameSettings.variant_percentages?.[variant] || {
      cash_percentage: 50,
      credit_percentage: 60
    };

    // Calculate in USD first
    const cashPriceUSD = buyPriceUSD * (variantSettings.cash_percentage / 100);
    const creditPriceUSD = buyPriceUSD * (variantSettings.credit_percentage / 100);

    // Convert to user's currency
    const cashPrice = convertCurrency(cashPriceUSD);
    const creditPrice = convertCurrency(creditPriceUSD);
    const lowPrice = convertCurrency(buyPriceUSD); // Base price used for calculation

    // Apply floor price (floor price should be in user's currency)
    const floorPrice = videoGameSettings.floor_price || 0;
    if (cashPrice < floorPrice && creditPrice < floorPrice) {
      return { cashPrice: 0, creditPrice: 0, lowPrice: lowPrice, isBelowFloor: true };
    }

    return {
      cashPrice: Math.max(cashPrice, 0).toFixed(2),
      creditPrice: Math.max(creditPrice, 0).toFixed(2),
      lowPrice: Math.max(lowPrice, 0).toFixed(2),
      isBelowFloor: false
    };
  };

  const handleVariantChange = (gameId, variant) => {
    setSelectedVariants(prev => ({ ...prev, [gameId]: variant }));
  };

  const handleQuantityChange = (gameId, quantity) => {
    setSelectedQuantities(prev => ({ ...prev, [gameId]: Math.max(1, Math.min(4, quantity)) }));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Clear UPC if searching by name, or vice versa
    if (localSearchTerm && upcSearch) {
      setUpcSearch('');
      fetchVideoGames(1, localConsole, localSearchTerm, '');
    } else {
      fetchVideoGames(1, localConsole, localSearchTerm, upcSearch);
    }
  };

  const handleUpcSearch = (e) => {
    e.preventDefault();
    if (upcSearch) {
      // Clear name search when searching by UPC
      setLocalSearchTerm('');
      fetchVideoGames(1, '', '', upcSearch);
    }
  };

  const handleAddToCart = (game, isCash) => {
    const variant = selectedVariants[game.id] || videoGameSettings.default_variant || 'cib';
    const quantity = selectedQuantities[game.id] || 1;
    const { cashPrice, creditPrice, lowPrice } = calculatePrice(game, variant);

    // Ensure name is a string
    const gameName = String(game.productName || game['product-name'] || 'Unknown Game');

    const cartItem = {
      productId: game.id,
      name: gameName,
      gameName: 'Video Games',
      expansionName: String(game.consoleName || game['console-name'] || ''),
      consoleName: String(game.consoleName || game['console-name'] || ''),
      image: game.imageUrl || game['image-url'] || '',
      variant,
      condition: VARIANT_LABELS[variant],
      printing: variant.toUpperCase(),
      quantity,
      cashPrice: parseFloat(cashPrice) || 0,
      creditPrice: parseFloat(creditPrice) || 0,
      lowPrice: parseFloat(lowPrice) || 0, // Base price used for calculation (retail buy price)
      basePrice: parseFloat(lowPrice) || 0, // Also set as basePrice for consistency
      originalBasePrice: parseFloat(lowPrice) || 0, // Original base price
      isCash,
      isVideoGame: true,
      isCustomItem: true, // Mark as custom item so App.js adds directly to cart
      source: game.source || 'pricecharting',
      cartItemId: `vg-${game.id}-${variant}-${Date.now()}`
    };

    // Call addToCart with just the item - App.js handles isCustomItem specially
    addToCart(cartItem);
  };

  const getEnabledVariants = () => {
    return (videoGameSettings.enabled_variants || ['loose', 'cib', 'new']).filter(v => {
      const settings = videoGameSettings.variant_percentages?.[v];
      return settings?.enabled !== false;
    });
  };

  const enabledVariants = getEnabledVariants();

  return (
    <div className="video-games-list">
      {/* Search and Filter Bar */}
      <div className="video-games-search-bar">
        <form onSubmit={handleSearch} className="video-games-search-form">
          <select
            value={localConsole}
            onChange={(e) => handleConsoleChange(e.target.value)}
            className="video-games-console-select"
          >
            <option value="">{t('All Consoles')}</option>
            {consoles.map((consoleName, idx) => (
              <option key={idx} value={consoleName}>{consoleName}</option>
            ))}
          </select>
          <input
            type="text"
            placeholder={t('Search by name...')}
            value={localSearchTerm}
            onChange={(e) => setLocalSearchTerm(e.target.value)}
            className="video-games-search-input"
          />
          <button type="submit" className="video-games-search-button">
            {t('Search')}
          </button>
        </form>
        <form onSubmit={handleUpcSearch} className="video-games-upc-form">
          <input
            type="text"
            placeholder={t('Scan barcode / UPC...')}
            value={upcSearch}
            onChange={(e) => setUpcSearch(e.target.value)}
            className="video-games-upc-input"
          />
          <button type="submit" className="video-games-upc-button">
            {t('Lookup')}
          </button>
        </form>
      </div>

      {/* Results count */}
      {!isLoading && totalCount > 0 && (
        <div className="video-games-results-count">
          {t('Showing')} {videoGames.length} {t('of')} {totalCount} {t('results')}
        </div>
      )}

      {/* Loading State */}
      {isLoading && <div className="video-games-loading">{t('Loading...')}</div>}

      {/* Games Grid - Uses same class names as CardList for consistent theming */}
      {!isLoading && (
        <div className="card-list video-games-grid">
          {videoGames
            .filter(game => {
              // Filter out games with $0 prices or below floor
              const selectedVariant = selectedVariants[game.id] || videoGameSettings.default_variant || 'cib';
              const { cashPrice, creditPrice, isBelowFloor } = calculatePrice(game, selectedVariant);

              // Hide if below floor or both prices are 0
              if (isBelowFloor) return false;
              if (parseFloat(cashPrice) === 0 && parseFloat(creditPrice) === 0) return false;

              return true;
            })
            .map(game => {
              const selectedVariant = selectedVariants[game.id] || videoGameSettings.default_variant || 'cib';
              const { cashPrice, creditPrice } = calculatePrice(game, selectedVariant);

              return (
                <div key={game.id} className="card video-game-card">
                  {game.imageUrl && !game.imageUrl.includes('grid.png') ? (
                    <img
                      src={game.imageUrl}
                      alt={game.productName}
                      className="card-image"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className="video-game-no-image card-image" style={{ display: game.imageUrl && !game.imageUrl.includes('grid.png') ? 'none' : 'flex' }}>
                    <span>🎮</span>
                    <span>No Image</span>
                  </div>
                  <div className="card-info">
                    <h3 className="card-name">{game.productName}</h3>
                    <p className="card-expansion">{game.consoleName}</p>

                    {/* Variant Selector */}
                    <div className="card-options">
                      <select
                        className="condition-select"
                        value={selectedVariant}
                        onChange={(e) => handleVariantChange(game.id, e.target.value)}
                      >
                        {enabledVariants.map(v => (
                          <option key={v} value={v}>{VARIANT_LABELS[v]}</option>
                        ))}
                      </select>
                    </div>

                    {/* Prices */}
                    <div className="card-actions">
                      {parseFloat(creditPrice) > 0 && (
                        <button
                          className="add-to-cart-button credit"
                          onClick={() => handleAddToCart(game, false)}
                        >
                          {t('Credit')} {currencySymbol}{creditPrice}
                        </button>
                      )}
                      {parseFloat(cashPrice) > 0 && (
                        <button
                          className="add-to-cart-button cash"
                          onClick={() => handleAddToCart(game, true)}
                        >
                          {t('Cash')} {currencySymbol}{cashPrice}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <div className="video-games-pagination">
          <button
            disabled={currentPage <= 1}
            onClick={() => fetchVideoGames(currentPage - 1)}
          >
            {t('Previous')}
          </button>
          <span>{t('Page')} {currentPage} {t('of')} {totalPages}</span>
          <button
            disabled={currentPage >= totalPages}
            onClick={() => fetchVideoGames(currentPage + 1)}
          >
            {t('Next')}
          </button>
        </div>
      )}

      {/* No Results */}
      {!isLoading && videoGames.length === 0 && (
        <div className="video-games-no-results">
          {t('No video games found. Try a different search term or console.')}
        </div>
      )}
    </div>
  );
};

export default VideoGamesList;

